/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { createSelector, createSlice } from '@reduxjs/toolkit';
import { ThunkStatus } from '../../enums/ThunkStatus';
import { findSubmissionByApplicationNumber, findSubmissionByPublicationNumber } from './autofill.thunk';

/* Initial State */
const initialState = {
  status: ThunkStatus.IDLE,
  submission: {},
  autoFillFields: [],
  autofillDialog: {
    open: false,
    loading: false,
    enableFillManually: false,
  },
  confirmAutofillDialog: {
    open: false,
    loading: false,
  },
  unitaryEffectWarningDialog: {
    open: false,
  },
  error: '',
};

/* Slice */
export const autofillSlice = createSlice({
  name: 'autofillSlice',
  initialState,
  reducers: {
    updateAutofillSubmission(state, action) {
      state.submission = action.payload;
    },
    updateAutofillFields(state, action) {
      state.autoFillFields = action.payload;
    },
    updateAutofillDialog(state, action) {
      state.autofillDialog = { ...state.autofillDialog, enableFillManually: false, ...action.payload };
    },
    updateConfirmAutofillDialog(state, action) {
      state.confirmAutofillDialog = action.payload;
    },
    updateUnitaryEffectWarningDialog(state, action) {
      state.unitaryEffectWarningDialog = {
        ...state.unitaryEffectWarningDialog,
        enableFillManually: false,
        ...action.payload,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(findSubmissionByApplicationNumber.pending, (state) => {
        state.status = ThunkStatus.LOADING;
        state.autofillDialog.loading = true;
        state.autofillDialog.enableFillManually = false;
      })
      .addCase(findSubmissionByApplicationNumber.fulfilled, (state, action) => {
        state.status = ThunkStatus.SUCCEEDED;
        state.error = undefined;

        if (!action.payload?.fields) {
          state.autofillDialog.loading = false;
          state.autofillDialog.enableFillManually = false;
          return;
        }

        state.autoFillFields = action.payload.fields;
        state.autofillDialog = { open: false, loading: false };
        state.confirmAutofillDialog.open = true;
        state.autofillDialog.enableFillManually = false;
      })
      .addCase(findSubmissionByApplicationNumber.rejected, (state, action) => {
        state.autofillDialog.loading = false;
        if (action.payload?.error?.status === 409) {
          state.autofillDialog.enableFillManually = false;
          state.unitaryEffectWarningDialog = { open: true };
        } else {
          state.status = ThunkStatus.FAILED;
          state.autofillDialog.enableFillManually = true;
          state.error = action?.error?.message;
        }
      })
      .addCase(findSubmissionByPublicationNumber.pending, (state) => {
        state.status = ThunkStatus.LOADING;
        state.autofillDialog.loading = true;
        state.autofillDialog.enableFillManually = false;
      })
      .addCase(findSubmissionByPublicationNumber.fulfilled, (state, action) => {
        state.status = ThunkStatus.SUCCEEDED;
        state.error = undefined;

        if (!action.payload?.fields) {
          state.autofillDialog.loading = false;
          state.autofillDialog.enableFillManually = false;
          return;
        }

        state.autoFillFields = action.payload.fields;
        state.autofillDialog = { open: false, loading: false };
        state.confirmAutofillDialog.open = true;
        state.autofillDialog.enableFillManually = false;
      })
      .addCase(findSubmissionByPublicationNumber.rejected, (state, action) => {
        state.autofillDialog.loading = false;
        if (action.payload?.error?.status === 409) {
          state.autofillDialog.enableFillManually = false;
          state.unitaryEffectWarningDialog = { open: true };
        } else {
          state.status = ThunkStatus.FAILED;
          state.autofillDialog.enableFillManually = true;
          state.error = action?.error?.message;
        }
      });
  },
});

/* Selectors */
const selectAutofill = state => state[autofillSlice.name];
export const selectAutofillSubmission = createSelector(selectAutofill, s => s.submission);
export const selectAutofillFields = createSelector(selectAutofill, s => s.autoFillFields);
export const selectAutofillDialogState = createSelector(selectAutofill, s => s.autofillDialog);
export const selectConfirmAutofillDialogState = createSelector(selectAutofill, s => s.confirmAutofillDialog);
export const selectUnitaryEffectWarningDialogState = createSelector(selectAutofill, s => s.unitaryEffectWarningDialog);

/* Actions */
export const {
  updateAutofillSubmission,
  updateAutofillFields,
  updateAutofillDialog,
  updateConfirmAutofillDialog,
  updateUnitaryEffectWarningDialog,
} = autofillSlice.actions;
